/**
 * Telemedicine · Phase 1 · Reactive 5-bar mic soundwave.
 *
 * Drop-in replacement for the static <Soundwave /> previously inlined in
 * LobbyControlPill + IncallControlsBar. Animates the bar heights in response
 * to the local mic level reported by Agora's `onAudioVolumeIndication`
 * (exposed as `localAudioLevel` on useAgoraCall, normalised 0..1).
 *
 * Why a single shared scale value (not per-bar):
 *   - The base heights [10, 6, 10, 14, 6] already give the wave a non-uniform
 *     silhouette, so a single uniform scale reads as a meter that "breathes"
 *     with the user's voice without looking like a synthetic equaliser.
 *   - Animated.timing on a single Animated.Value avoids the cost of N parallel
 *     animations every 200ms (the Agora callback cadence).
 *
 * Visual range: bars sit at ~30 % of base height when silent, expand to ~140 %
 * when the user is speaking loudly. Tuned against actual mic input on iOS — at
 * the floor, bars are still visible (so the icon doesn't look broken when the
 * mic is on but the user is silent), and at the ceiling, the tallest bar
 * (~20 px) still fits inside the 24 px iconStack used by both consumers.
 */
import React, { useEffect, useRef } from 'react';
import { View, Animated, StyleSheet } from 'react-native';

interface AnimatedSoundwaveProps {
  /** Local mic level, 0..1. Defaults to 0 (idle bars). */
  level?: number;
  /** Bar fill colour. */
  color?: string;
}

const BLUE_HORIZON = '#669AB7';
const BASE_HEIGHTS = [10, 6, 10, 14, 6];

export default function AnimatedSoundwave({
  level = 0,
  color = BLUE_HORIZON,
}: AnimatedSoundwaveProps) {
  // Single shared scale, interpolated per-bar at render time. Starts at the
  // idle floor so the first frame doesn't pop from 0 → idle.
  const scale = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    // Map level 0..1 → scale 0.3..1.4. Clamp upstream values defensively in
    // case Agora ever reports something outside the documented range.
    const clamped = Math.max(0, Math.min(1, level));
    const target = 0.3 + clamped * 1.1;
    Animated.timing(scale, {
      toValue: target,
      // 120 ms feels in-sync with the 200 ms volume callback without the
      // overshoot that <80 ms produces on jittery audio.
      duration: 120,
      useNativeDriver: false,
    }).start();
  }, [level, scale]);

  return (
    <View style={styles.wave}>
      {BASE_HEIGHTS.map((h, i) => (
        <Animated.View
          key={i}
          style={[
            styles.bar,
            {
              backgroundColor: color,
              // Clamp output so the tallest bar (base 14) never exceeds the
              // 24 px icon stack: 14 * 1.4 = 19.6, well within budget.
              height: scale.interpolate({
                inputRange: [0.3, 1.4],
                outputRange: [h * 0.3, h * 1.4],
                extrapolate: 'clamp',
              }),
            },
          ]}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  wave: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 24,
  },
  bar: {
    width: 2,
    borderRadius: 12,
  },
});
